import {
  Text,
  Code,
  Icon,
  InlineStack,
  FormattedDateTime,
  Spinner,
  Flex,
  Divider,
} from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';
import { hydraUiApi } from '@/services/api';
import { useTemplateOptional } from '@/services/db/template/queries';
import { isEmptyString } from '@/utils/string';
import { TEMPLATE_TYPE_ICONS, TEMPLATE_TYPE_LABELS } from '../constants';
import { MetadataRow } from './metadata-row';
import { HtmlPreviewFrame } from './html-preview-frame';

type Props = { result: Extract<SearchResult, { kind: 'template' }> };

const PREVIEWABLE_TEMPLATE_TYPES = new Set(['email']);

export function TemplatePreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const templateId = Number(result.other.id);
  const isPreviewable = PREVIEWABLE_TEMPLATE_TYPES.has(result.other.template_type);
  const templateIcon = TEMPLATE_TYPE_ICONS[result.other.template_type];
  const templateLabel = TEMPLATE_TYPE_LABELS[result.other.template_type];

  const {
    data: template,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
  } = useTemplateOptional({ workspaceId: envId, templateId, enabled: isPreviewable });

  const hasBody = template != null && !isEmptyString(template.body);

  const {
    data: previewData,
    isPending: isPreviewPending,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['templatePreview', envId, templateId, result.other.updated_at],
    queryFn: ({ signal }) =>
      hydraUiApi.previews.email({
        params: { path: { environment_id: String(envId) } },
        body: {
          body: template!.body,
          subject: template!.subject,
          layout: '{{content}}',
          preprocessor: template!.preprocessor,
          headers: [],
          variables: {},
        },
        signal,
      }),
    enabled: hasBody,
    staleTime: 5 * 60 * 1000,
  });

  const renderedHtml = previewData?.body;
  const isLoading = isTemplateLoading || (hasBody && isPreviewPending);
  const isError = isTemplateError || isPreviewError;

  return (
    <>
      {isPreviewable ? (
        <>
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" py="200">
              <Spinner />
            </Flex>
          ) : isError ? (
            <Text size="xs" color="subtle">
              Failed to load preview
            </Text>
          ) : !isEmptyString(renderedHtml) ? (
            <HtmlPreviewFrame html={renderedHtml ?? ''} title="Template preview" />
          ) : null}
          {isLoading || isError || !isEmptyString(renderedHtml) ? <Divider mt="100" /> : null}
        </>
      ) : null}
      <Flex direction="column" gap="100" mt={isPreviewable ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        <MetadataRow label="Type">
          <InlineStack gap="050" alignBlock="center">
            <Icon name={templateIcon ?? 'email'} size="sm" color="subtle" />
            <Text size="xs">{templateLabel ?? 'Template'}</Text>
          </InlineStack>
        </MetadataRow>
        {result.other.subject != null ? (
          <MetadataRow label="Subject">
            <Text size="xs" className="fs-mask">
              {result.other.subject}
            </Text>
          </MetadataRow>
        ) : null}
        {result.other.language != null ? (
          <MetadataRow label="Language">
            <Text size="xs">{result.other.language}</Text>
          </MetadataRow>
        ) : null}
        <MetadataRow label="Created">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}
